// Risk tab — tracking error, drawdown/underwater, multi-horizon tail (VaR/CVaR),
// return-distribution detail, rolling trajectories for every metric, market sensitivity,
// concentration/exposure. Full Account→Basket→Strategy scope bar (same as Performance) +
// a period selector. vs SPY. Plan: Plans/performance_and_risk_tabs.md §4.
import React, { useEffect, useMemo, useState } from 'react';
import { tradingService } from '../../services/tradingService';
import { useTheme } from '../../context/ThemeContext';
import ScopeBar from './portfolio/ScopeBar';

const pct = (v, dp = 1) => (v == null || isNaN(v) ? '—' : `${(v * 100).toFixed(dp)}%`);
const num = (v, dp = 2) => (v == null || isNaN(v) ? '—' : Number(v).toFixed(dp));
const neg = (v) => (v == null || isNaN(v) ? 'text-gray-800 dark:text-gray-200' : v < 0 ? 'text-red-500' : 'text-gray-800 dark:text-gray-200');
const posneg = (v) => (v == null || isNaN(v) ? 'text-gray-800 dark:text-gray-200' : v < 0 ? 'text-red-500' : 'text-emerald-500');

const Tile = ({ label, value, valueClass = 'text-gray-800 dark:text-gray-200', sub }) => (
  <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-3">
    <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">{label}</div>
    <div className={`text-lg font-semibold tabular-nums ${valueClass}`}>{value}</div>
    {sub && <div className="text-[10px] text-gray-400 mt-0.5">{sub}</div>}
  </div>
);

const Section = ({ title, children }) => (
  <div>
    <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-2">{title}</div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{children}</div>
  </div>
);

// ── period presets → start date relative to the data's last date ────────────
const RANGES = ['1M', '3M', '6M', 'YTD', '1Y', 'ALL'];
const startForRange = (range, dateMax) => {
  if (!dateMax || range === 'ALL') return null;
  const d = new Date(`${dateMax}T00:00:00`);
  const iso = (x) => x.toISOString().slice(0, 10);
  if (range === 'YTD') return `${dateMax.slice(0, 4)}-01-01`;
  const map = { '1M': 1, '3M': 3, '6M': 6, '1Y': 12 };
  const m = map[range];
  if (m) { d.setMonth(d.getMonth() - m); return iso(d); }
  return null;
};

// rolling-metric catalogue — how each series is read + formatted + baselined.
const ROLL_METRICS = [
  { key: 'sharpe', label: 'Sharpe', fmt: (v) => num(v, 2), zero: true },
  { key: 'sortino', label: 'Sortino', fmt: (v) => num(v, 2), zero: true },
  { key: 'vol', label: 'Volatility', fmt: (v) => pct(v, 1), zero: false },
  { key: 'downside_dev', label: 'Downside Dev', fmt: (v) => pct(v, 1), zero: false },
  { key: 'ann_return', label: 'Ann. Return', fmt: (v) => pct(v, 1), zero: true },
  { key: 'var95', label: 'VaR 95%', fmt: (v) => pct(v, 2), zero: true },
  { key: 'cvar95', label: 'CVaR 95%', fmt: (v) => pct(v, 2), zero: true },
  { key: 'max_drawdown', label: 'Max Drawdown', fmt: (v) => pct(v, 2), zero: true },
  { key: 'beta', label: 'Beta', fmt: (v) => num(v, 2), zero: true },
  { key: 'tracking_error', label: 'Tracking Error', fmt: (v) => pct(v, 1), zero: false },
  { key: 'correlation', label: 'Correlation', fmt: (v) => num(v, 2), zero: true },
];

// generic trajectory line chart — reads points[valueKey], optional zero baseline.
const TrajectoryChart = ({ points, valueKey, fmt, zero, color = '#c9a96e' }) => {
  const model = useMemo(() => {
    const pts = (points || []).filter((p) => p[valueKey] != null && !isNaN(p[valueKey]));
    if (pts.length < 2) return null;
    const W = 640, H = 150, pad = 8;
    const vals = pts.map((p) => p[valueKey]);
    let lo = Math.min(...vals), hi = Math.max(...vals);
    if (zero) { lo = Math.min(lo, 0); hi = Math.max(hi, 0); }
    const span = hi - lo || 1;
    const n = pts.length;
    const xOf = (i) => pad + (i / (n - 1)) * (W - 2 * pad);
    const yOf = (v) => pad + (1 - (v - lo) / span) * (H - 2 * pad);
    const line = pts.map((p, i) => `${i ? 'L' : 'M'}${xOf(i).toFixed(1)},${yOf(p[valueKey]).toFixed(1)}`).join(' ');
    const zeroY = zero && lo < 0 && hi > 0 ? yOf(0) : null;
    return { W, H, line, lo, hi, last: vals[vals.length - 1], first: pts[0].date, lastDate: pts[n - 1].date, zeroY };
  }, [points, valueKey, zero]);
  if (!model) return <div className="text-xs text-gray-400 italic py-8 text-center">Not enough history for a rolling trajectory yet — the window fills as EOD snapshots accrue.</div>;
  return (
    <div>
      <svg viewBox={`0 0 ${model.W} ${model.H}`} preserveAspectRatio="none" className="w-full" style={{ height: 150 }}>
        {model.zeroY != null && <line x1="0" x2={model.W} y1={model.zeroY} y2={model.zeroY} stroke="currentColor" className="text-gray-300 dark:text-gray-700" strokeWidth="1" strokeDasharray="3 3" />}
        <path d={model.line} fill="none" stroke={color} strokeWidth="1.75" />
      </svg>
      <div className="flex justify-between text-[10px] text-gray-400 mt-1 tabular-nums">
        <span>{model.first} · min {fmt(model.lo)}</span>
        <span className="text-gray-500 dark:text-gray-300">latest {fmt(model.last)}</span>
        <span>max {fmt(model.hi)} · {model.lastDate}</span>
      </div>
    </div>
  );
};

// Underwater (drawdown) area — always ≤ 0, red fill from the 0 line downward.
const Underwater = ({ points }) => {
  const model = useMemo(() => {
    if (!points || points.length < 2) return null;
    const W = 640, H = 120, pad = 4;
    const lo = Math.min(-0.0001, ...points.map((p) => p.dd));
    const n = points.length;
    const xOf = (i) => pad + (i / (n - 1)) * (W - 2 * pad);
    const yOf = (v) => pad + (v / lo) * (H - 2 * pad);
    const line = points.map((p, i) => `${i ? 'L' : 'M'}${xOf(i).toFixed(1)},${yOf(p.dd).toFixed(1)}`).join(' ');
    const area = `${line} L${xOf(n - 1).toFixed(1)},${pad} L${pad},${pad} Z`;
    return { W, H, line, area };
  }, [points]);
  if (!model) return <div className="text-xs text-gray-400 italic py-6 text-center">Not enough data for the underwater curve.</div>;
  return (
    <svg viewBox={`0 0 ${model.W} ${model.H}`} preserveAspectRatio="none" className="w-full" style={{ height: 120 }}>
      <path d={model.area} fill="#ef4444" fillOpacity="0.18" />
      <path d={model.line} fill="none" stroke="#ef4444" strokeWidth="1.5" />
    </svg>
  );
};

// VaR/CVaR matrix — horizons × confidence. Cells flag √-time-scaled fallbacks.
const HORIZONS = [['daily', 'Daily'], ['weekly', 'Weekly'], ['monthly', 'Monthly']];
const VarMatrix = ({ matrix, confidences }) => {
  if (!matrix || !Object.keys(matrix).length) return null;
  const anyScaled = HORIZONS.some(([h]) => confidences.some((c) => matrix[h]?.[c]?.method === 'scaled'));
  return (
    <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 overflow-x-auto scrollbar-thin">
      <table className="w-full text-sm tabular-nums">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-gray-400">
            <th className="text-left font-medium py-1.5 pr-3">Horizon</th>
            {confidences.map((c) => (
              <React.Fragment key={c}>
                <th className="text-right font-medium py-1.5 px-2">VaR {c}%</th>
                <th className="text-right font-medium py-1.5 px-2">CVaR {c}%</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {HORIZONS.map(([h, label]) => (
            <tr key={h} className="border-t border-gray-100 dark:border-gray-800">
              <td className="text-left py-1.5 pr-3 text-gray-600 dark:text-gray-300">
                {label}
                {confidences.some((c) => matrix[h]?.[c]?.method === 'scaled') && (
                  <span className="ml-1 text-[9px] text-amber-500 align-top">~</span>
                )}
              </td>
              {confidences.map((c) => {
                const cell = matrix[h]?.[c] || {};
                return (
                  <React.Fragment key={c}>
                    <td className="text-right py-1.5 px-2 text-red-500">{pct(cell.var, 2)}</td>
                    <td className="text-right py-1.5 px-2 text-red-500">{pct(cell.cvar, 2)}</td>
                  </React.Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {anyScaled && (
        <div className="text-[10px] text-gray-400 mt-2">
          <span className="text-amber-500">~</span> √-time scaled from the daily figure (fewer than 20 non-overlapping
          periods in-window). Cells fill with true resampled {`{weekly, monthly}`} history as it accrues.
        </div>
      )}
    </div>
  );
};

const RiskPage = () => {
  const { isDark } = useTheme();
  const accent = isDark ? '#c9a96e' : '#3b82f6';
  const [scope, setScope] = useState(null);          // {accountIds, portfolioIds, strategyIds, level}
  const [benchmark] = useState('SPY');
  const [range, setRange] = useState('ALL');
  const [custom, setCustom] = useState({ start: '', end: '' });
  const [rollWindow, setRollWindow] = useState(21);
  const [rollMetric, setRollMetric] = useState('sharpe');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const winParams = useMemo(() => {
    if (range === 'CUSTOM') return { start: custom.start || undefined, end: custom.end || undefined };
    const start = startForRange(range, data?.date_max);
    return { start: start || undefined, end: undefined };
  }, [range, custom, data?.date_max]);

  useEffect(() => {
    if (!scope) return;
    setLoading(true); setError(null);
    tradingService.getPortfolioRisk({
      accountIds: scope.accountIds, portfolioIds: scope.portfolioIds, strategyIds: scope.strategyIds,
      benchmark, start: winParams.start, end: winParams.end, rollingWindow: rollWindow,
    })
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.error || 'Failed to load risk'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, benchmark, winParams.start, winParams.end, rollWindow]);

  const s = data?.stats || {};
  const c = data?.concentration || {};
  const dist = data?.distribution || {};
  const confidences = ['95', '99'];
  const metricDef = ROLL_METRICS.find((m) => m.key === rollMetric) || ROLL_METRICS[0];
  const scopeLabel = scope?.level === 'basket' ? 'basket' : scope?.level === 'strategy' ? 'strategy' : 'portfolio';

  return (
    <div className="max-w-6xl mx-auto space-y-5">
      <div>
        <h2 className="text-xl font-serif text-gray-800 dark:text-gray-100 flex items-center">
          Risk
          {loading && data && <span className="ml-2 inline-block w-3.5 h-3.5 border-2 border-brand-gold/70 border-t-transparent rounded-full animate-spin" />}
        </h2>
        <p className="text-[11px] text-gray-400">
          {scopeLabel} scope · vs {data?.benchmark || benchmark}
          {data?.window ? ` · ${data.window[0]} → ${data.window[1]} · ${data.n} days` : ''}
        </p>
      </div>

      {/* scope bar — Account → Basket → Strategy */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-3">
        <ScopeBar onChange={setScope} />
      </div>

      {/* period selector */}
      <div className="flex items-center gap-1 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider text-gray-400 mr-1">Period</span>
        {RANGES.map((r) => (
          <button key={r} onClick={() => setRange(r)}
            className={`px-2.5 py-1 text-[11px] rounded-md border transition-colors ${
              range === r ? 'bg-brand-gold/10 text-brand-gold border-brand-gold/40 font-semibold'
                : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
          >{r}</button>
        ))}
        <button onClick={() => setRange('CUSTOM')}
          className={`px-2.5 py-1 text-[11px] rounded-md border transition-colors ${
            range === 'CUSTOM' ? 'bg-brand-gold/10 text-brand-gold border-brand-gold/40 font-semibold'
              : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
        >Custom</button>
        {range === 'CUSTOM' && (
          <span className="flex items-center gap-1">
            <input type="date" value={custom.start} min={data?.date_min} max={data?.date_max}
              onChange={(e) => setCustom((p) => ({ ...p, start: e.target.value }))}
              className="text-[11px] bg-transparent border border-gray-200 dark:border-gray-700 rounded px-1.5 py-1" />
            <span className="text-gray-400 text-[11px]">→</span>
            <input type="date" value={custom.end} min={data?.date_min} max={data?.date_max}
              onChange={(e) => setCustom((p) => ({ ...p, end: e.target.value }))}
              className="text-[11px] bg-transparent border border-gray-200 dark:border-gray-700 rounded px-1.5 py-1" />
          </span>
        )}
      </div>

      {error && <div className="text-sm text-red-500">{error}</div>}
      {loading && !data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-[68px] skeleton rounded-lg" />)}
        </div>
      )}

      {data && data.n > 0 && (<>
        <Section title="Drawdown & volatility">
          <Tile label="Max Drawdown" value={pct(s.max_drawdown)} valueClass={neg(s.max_drawdown)} />
          <Tile label="Current Drawdown" value={pct(s.current_drawdown)} valueClass={neg(s.current_drawdown)} />
          <Tile label="Volatility" value={pct(s.vol)} sub="annualised" />
          <Tile label="Downside Dev" value={pct(s.downside_dev)} sub="annualised" />
        </Section>
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Underwater</div>
          <Underwater points={data.underwater} />
        </div>

        {/* rolling trajectory of any metric */}
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
            <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
              Rolling trajectory · {rollWindow}-day window
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select value={rollMetric} onChange={(e) => setRollMetric(e.target.value)}
                className="text-[11px] bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-md px-2 py-1">
                {ROLL_METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
              <div className="inline-flex rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden">
                {[10, 21, 63].map((w) => (
                  <button key={w} onClick={() => setRollWindow(w)}
                    className={`px-2 py-1 text-[11px] ${rollWindow === w ? 'bg-brand-gold/10 text-brand-gold font-semibold' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                  >{w}d</button>
                ))}
              </div>
            </div>
          </div>
          <TrajectoryChart points={data.rolling} valueKey={metricDef.key} fmt={metricDef.fmt} zero={metricDef.zero} color={accent} />
        </div>

        <div>
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-2">
            Tail risk — VaR / CVaR by horizon (historical, in-window)
          </div>
          <VarMatrix matrix={data.var_matrix} confidences={confidences} />
        </div>

        <Section title="Return distribution">
          <Tile label="Ann. Return" value={pct(dist.ann_return)} valueClass={posneg(dist.ann_return)} sub="in-window, annualised" />
          <Tile label="Sharpe" value={num(dist.sharpe)} valueClass={posneg(dist.sharpe)} sub="rf = 0" />
          <Tile label="Sortino" value={num(dist.sortino)} valueClass={posneg(dist.sortino)} />
          <Tile label="% Positive Days" value={pct(dist.pct_positive)} />
          <Tile label="Best Day" value={pct(dist.best_day, 2)} valueClass="text-emerald-500" />
          <Tile label="Worst Day" value={pct(dist.worst_day, 2)} valueClass="text-red-500" />
          <Tile label="Avg Gain / Loss" value={`${pct(dist.avg_gain, 2)} / ${pct(dist.avg_loss, 2)}`} />
          <Tile label="Skew / Kurtosis" value={`${num(dist.skew)} / ${num(dist.kurtosis)}`} sub="excess kurtosis" />
        </Section>

        <Section title={`Market sensitivity vs ${data.benchmark}`}>
          <Tile label="Tracking Error" value={pct(s.tracking_error)} sub="annualised" />
          <Tile label="Beta" value={num(s.beta)} />
          <Tile label="Correlation" value={num(s.correlation)} />
          <Tile label="Up / Down Capture" value={`${num(s.up_capture)} / ${num(s.down_capture)}`} />
        </Section>

        <Section title="Concentration & exposure">
          <Tile label="Largest Position" value={pct(c.largest_pct)} sub={`${c.n_positions || 0} holdings`} />
          <Tile label="Top-10 Weight" value={pct(c.top10_pct)} />
          <Tile label="HHI" value={num(c.hhi, 3)} sub="Herfindahl" />
          <Tile label="Cash" value={pct(data.cash_pct)} sub={`gross $${Number(data.gross_exposure || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`} />
        </Section>
      </>)}

      {data && data.n === 0 && !loading && (
        <p className="text-sm text-gray-400 italic py-6">No return history in scope yet{range !== 'ALL' ? ' for this period' : ''}.</p>
      )}
    </div>
  );
};

export default RiskPage;
